import React, {
    ReactElement, useEffect, useState,
} from "react";
import {
    DragDropContext, Draggable, Droppable, DropResult,
} from "react-beautiful-dnd";
import { Form } from "react-bootstrap";
import { FiTrash2 } from "react-icons/fi";
import { RiPencilLine } from "react-icons/ri";
import Confirm from "../../../../components/confirm/Confirm";
import Editor from "../../../../components/editor/Editor";
import ExpandingSection from "../../../../components/expanding-section/ExpandingSection";
import FieldTypeValueDisplay, { DEFAULT_VALUE_REPLACEMENTS } from "../../../../components/form-field/FieldTypeValueDisplay";
import FormFieldEdit from "../../../../components/form-field/FormField";
import { HtmlRenderer } from "../../../../components/html-render/HtmlRenderer";
import BooleanSelect from "../../../../components/pickers/boolean/BooleanSelect";
import Select from "../../../../components/select/Select";
import {
    FieldTypedReplaceableValue, FieldTypedValue,
    FieldTypedValueBaseInput,
    FormField, FormFieldDescriptorInput, FormFieldReplacementInput, FormFieldVisibility, GetFormDataQuery,
} from "../../../../__generated__/api";
import { CustomFieldModal } from "../custom-field/CustomField";

interface FormDesignerEditProps {
    field: FormFieldDescriptorInput | undefined;
    formField: FormField | undefined;
    onHide: () => void;
    onSave: (input: FormFieldDescriptorInput) => void;
    requestFormType: string | undefined;
}
function FormDesignerEdit({
    field: inputField, formField, onHide, onSave, requestFormType,
}: FormDesignerEditProps): ReactElement {
    const [field, setField] = useState<FormFieldDescriptorInput>();
    useEffect(() => {
        setField(inputField);
    }, [inputField]);

    const setValue = (value: Partial<FormFieldDescriptorInput>) => {
        setField({
            ...field,
            ...value,
        });
    };
    return (
        <Confirm
            shouldShow={inputField != null}
            onHide={onHide}
            confirmText="Save"
            onConfirm={() => {
                if (field) {
                    onSave(field);
                }
            }}
        >
            <div>
                <h4>
                    Overrides
                </h4>
                <p className="text-gray-600">
                    Use these options to override the field level settings. Overriding options has no affect
                    on the source field and will only apply to fields when displayed in this form.
                </p>
                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">
                        Required
                    </Form.Label>
                    <BooleanSelect
                        isRequired
                        value={field?.isRequired ?? false}
                        onChange={isRequired => setValue({ isRequired })}
                    />
                    <Form.Text>
                        Make this field required when filling out this form.
                    </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">
                        Name
                    </Form.Label>
                    <Form.Control
                        type="text"
                        value={field?.name ?? ""}
                        onChange={evt => setValue({ name: evt.target.value })}
                    />
                    <Form.Text>
                        Override the name of the field that gets displayed to the user.
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">
                        Description
                    </Form.Label>
                    <div className="bg-white border-light border rounded">
                        <Editor
                            text={field?.description ?? ""}
                            onChange={description => setValue({ description })}
                        />
                    </div>
                    <Form.Text>
                        Descriptions or instructions that show up next to the question to give the user more context.
                    </Form.Text>
                </Form.Group>
                <ExpandingSection title="Advanced Settings" classBackground="bg-gray-100">
                    <>
                        {requestFormType !== "action" && (
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder">
                                    Required for close
                                </Form.Label>
                                <BooleanSelect
                                    value={field?.isRequiredForAgent ?? false}
                                    isRequired
                                    onChange={value => setValue({ isRequiredForAgent: value ?? false })}
                                />
                                <Form.Text>
                                    Does the agent need to complete this field before the ticket can be closed?
                                </Form.Text>
                            </Form.Group>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bolder">
                                Example
                                {" "}
                                <small className="text-light">(Optional)</small>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Example text"
                                onChange={evt => setValue({ placeholder: evt.target.value })}
                                value={field?.placeholder ?? ""}
                            />
                            <Form.Text>
                                This text appears in the text by default. This is usually an example of what
                                the user should enter.
                            </Form.Text>
                        </Form.Group>
                        {formField && (
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder">
                                    Default Value
                                </Form.Label>
                                <FieldTypeValueDisplay
                                    shouldShowFieldName={false}
                                    field={{
                                        ...formField,
                                        placeholder: field?.placeholder,
                                        isRequired: false,
                                    }}
                                    value={field?.defaultValue?.value as FieldTypedValue}
                                    replacements={field?.defaultValue?.replacements as FormFieldReplacementInput[]}
                                    onChange={(value, replacements) => setValue({
                                        defaultValue: {
                                            value: {
                                                ...value,
                                                fieldId: undefined,
                                            } as FieldTypedValueBaseInput,
                                            replacements,
                                        },
                                    })}
                                    replaceableInput={DEFAULT_VALUE_REPLACEMENTS}
                                />
                                <Form.Text>
                                    Set a default value for the field or no value if not required.
                                </Form.Text>
                            </Form.Group>
                        )}

                    </>
                </ExpandingSection>

            </div>
        </Confirm>
    );
}

enum CustomAction {
    ADD = "add",
    AGENT_TEXT = "agentText",
    REQUESTOR_TEXT = "requestorText"
}

type DescriptorSetter = (current: FormFieldDescriptorInput[]) => FormFieldDescriptorInput[];
export interface FormDesignerProps {
    formData: GetFormDataQuery | undefined;
    setFields: (handler: FormFieldDescriptorInput[] | DescriptorSetter) => void;
    fields: FormFieldDescriptorInput[];
    id: string | undefined;
    groupIds?: string[];
    formName: string;
    requestFormType?: string;
    onCustomFieldAdded: () => void;
}
export default function FormDesigner({
    formData, setFields, fields, id, formName, onCustomFieldAdded, groupIds, requestFormType,
}: FormDesignerProps): ReactElement {
    const [staticText, setStaticText] = useState<string>();
    const [currentField, setCurrentField] = useState<FormFieldDescriptorInput>();
    const [editingIndex, setEditingIndex] = useState<number>();
    const [editFieldId, setEditField] = useState<string>();

    const currentSet = new Set(fields.toIdList("fieldId"));
    const availableFields = formData?.formFields?.filter(field => !currentSet.has(field.id)
        && field.visibility !== FormFieldVisibility.Private)
        .groupBy(row => (row.isSystemField ? "system" : "custom")) as undefined | {
        system: FormField[],
        custom: FormField[]
    } ?? { system: [], custom: [] };
    const fieldMap = formData?.formFields?.toIdMap() ?? {};

    const onDragEnd = (result: DropResult) => {
        if (!result.source || !result.destination) {
            return;
        }
        const copy = Array.from(fields);
        const [item] = copy.splice(result.source.index, 1);
        copy.splice(result.destination?.index, 0, item);
        setFields(copy);
    };
    const removeField = (index: number) => {
        const copy = Array.from(fields);
        copy.splice(index, 1);
        setFields(copy);
    };
    const isAgentText = currentField?.fieldId === CustomAction.AGENT_TEXT;
    const isRequestorText = currentField?.fieldId === CustomAction.REQUESTOR_TEXT;

    const editTextField = (field: FormFieldDescriptorInput, index: number) => {
        const type: keyof FormFieldDescriptorInput = field.agentText ? CustomAction.AGENT_TEXT : CustomAction.REQUESTOR_TEXT;
        setStaticText(field[type] as string);
        setCurrentField({ fieldId: type });
        setEditingIndex(index);
    };

    return (
        <div className="rounded">

            <div className=" border alert bg-white p-3 rounded">

                <h5 className="">Form Designer</h5>

                <small className="text-light">
                    Select one of the fields from the list below. This will add a
                    field to the
                    {" "}
                    {requestFormType}
                    . Click and drag to reorder the fields.
                    You can also remove it by using the
                    {" "}
                    <FiTrash2 className="fs-8 text-light" />
                    {" "}
                    icon.
                </small>

                <div className="d-flex my-2">
                    <Select
                        className="text-gray w-100 p-0 m-0"
                        placeholder="Select a field.."
                        onChange={value => {
                            const field = {
                                fieldId: value.value,
                            };
                            setCurrentField(field);
                            if (value.value.startsWith("urn")) {
                                setFields(current => current.concat([field]));
                                setCurrentField(undefined);
                            }
                        }}
                        value={{
                            value: "",
                            label: "Select a field...",
                        }}
                        options={[
                            {
                                label: "Create",
                                options: [
                                    {
                                        label: "Create Custom Field",
                                        value: CustomAction.ADD,
                                    },
                                    {
                                        label: "Add Agent Note",
                                        value: CustomAction.AGENT_TEXT,
                                    },
                                    {
                                        label: "Add Requester Text",
                                        value: CustomAction.REQUESTOR_TEXT,
                                    },
                                ],
                            },
                            {
                                label: "System",
                                options: availableFields?.system?.map(row => ({
                                    label: row.name,
                                    value: row.id as string,
                                })) ?? [],
                            },
                            {
                                label: "Custom Fields",
                                options: availableFields?.custom?.map(row => ({
                                    label: row.name,
                                    value: row.id as string,
                                })) ?? [],
                            },

                        ]}

                    />

                </div>

                {fields.isNotEmpty && (

                    <Form.Group className="mt-3">
                        <Form.Label className="fw-bolder text-gray-600">
                            {formName}
                        </Form.Label>
                        <DragDropContext
                            nonce={window.NONCE}
                            onDragEnd={onDragEnd}
                        >

                            <Droppable
                                key="drop-columns"
                                droppableId="drop-columns"
                                mode="standard"
                                direction="vertical"
                            >
                                {dropStoppable => (
                                    <div
                                        ref={dropStoppable.innerRef}
                                        // eslint-disable-next-line react/jsx-props-no-spreading
                                        {...dropStoppable.droppableProps}
                                    >

                                        {fields.mapFilter((field, index) => {
                                            const { fieldId, requestorText, agentText } = field;
                                            const formField = fieldMap[field.fieldId];
                                            const fieldDescription = field.description || formField?.description;
                                            const text = agentText || requestorText;
                                            const editHandler = () => {
                                                if (text) {
                                                    editTextField(field, index);
                                                } else if (formField.visibility === FormFieldVisibility.Private) {
                                                    setCurrentField(field);
                                                    setEditingIndex(index);
                                                    setEditField(formField.id);
                                                } else {
                                                    setCurrentField(field);
                                                    setEditingIndex(index);
                                                }
                                            };
                                            return (!formField && !requestorText && !agentText) ? undefined : (
                                                <Draggable
                                                    draggableId={fieldId ?? text?.substr(0, 100)}
                                                    key={fieldId ?? text?.substr(0, 100)}
                                                    index={index}

                                                >
                                                    {(provided /* snapshot */) => (
                                                        <div
                                                            // eslint-disable-next-line react/jsx-props-no-spreading
                                                            {...provided.draggableProps}
                                                            // eslint-disable-next-line react/jsx-props-no-spreading
                                                            {...provided.dragHandleProps}
                                                            ref={provided.innerRef}
                                                            className={`"d-flex mb-3 ${(agentText || requestorText)
                                                                ? "bg-gray-100 p-2"
                                                                : ""}`}
                                                        >
                                                            <div className="bg-gray-400 rounded-start me-2 ps-2" />
                                                            <div className="flex-grow-1">
                                                                <div
                                                                    className="d-flex justify-content-between
                                                                                            align-items-center"
                                                                >
                                                                    <small
                                                                        className="text-gray-500"
                                                                        title={formField?.description as string}
                                                                    >
                                                                        {agentText && ("(Only visible to Agent)")}
                                                                        {requestorText && ("(Only visible to Requester)")}
                                                                        {field.name || formField?.name}
                                                                        {(field.name || formField?.name) && !field.isRequired && (
                                                                            <small className="text-light ms-2">(Optional)</small>
                                                                        )}
                                                                    </small>
                                                                    <div>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-none p-0 me-3"
                                                                            onClick={editHandler}
                                                                        >
                                                                            <RiPencilLine
                                                                                className="fs-8 text-light"
                                                                            />
                                                                        </button>

                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-none p-0"
                                                                            onClick={() => removeField(index)}
                                                                        >
                                                                            <FiTrash2 className="fs-8 text-light" />
                                                                        </button>
                                                                    </div>

                                                                </div>
                                                                {text ? (
                                                                    <div>
                                                                        <HtmlRenderer html={text} />
                                                                    </div>
                                                                ) : (
                                                                    <div>
                                                                        <FormFieldEdit
                                                                            isEditable
                                                                            isEditing
                                                                            shouldFocus={false}
                                                                            defaultValue={
                                                                                field.defaultValue as FieldTypedReplaceableValue
                                                                            }
                                                                            formField={{
                                                                                ...formField,
                                                                                placeholder: field.placeholder
                                                                                    ?? formField.placeholder,
                                                                                name: field?.name || formField?.name,
                                                                            }}
                                                                            isFormField
                                                                        />
                                                                        {fieldDescription && (
                                                                            <small>
                                                                                <HtmlRenderer html={fieldDescription} />
                                                                            </small>
                                                                        )}
                                                                    </div>
                                                                )}

                                                            </div>
                                                        </div>
                                                    )}
                                                </Draggable>

                                            );
                                        })}
                                        {dropStoppable.placeholder}
                                    </div>

                                )}
                            </Droppable>
                        </DragDropContext>

                    </Form.Group>

                )}

            </div>
            <div className="px-2">
                <small className="text-gray-600">
                    The preview above gives you an idea of what this
                    {" "}
                    {requestFormType}
                    {" "}
                    will look like for end users.
                    Depending on where the
                    {" "}
                    {requestFormType}
                    {" "}
                    is being displayed, it might look slightly different.
                </small>
            </div>

            {(currentField?.fieldId === "add" || editFieldId) && (
                <CustomFieldModal
                    id={editFieldId ?? "new"}
                    formField={currentField}
                    canSetPrivate
                    canDelete={false}
                    groupIds={groupIds}
                    requestFormType={requestFormType}
                    onDone={(createdId, formField) => {
                        if (createdId) {
                            setFields(current => {
                                const copy = Array.from(current);
                                if (editingIndex != null) {
                                    copy[editingIndex] = (formField ?? {
                                        fieldId: createdId,
                                    });
                                } else {
                                    copy.push(formField ?? {
                                        fieldId: createdId,
                                    });
                                }

                                return copy;
                            });
                            onCustomFieldAdded();
                        }
                        setCurrentField(undefined);
                        setEditField(undefined);
                        setEditingIndex(undefined);
                    }}
                    shouldPreview={false}
                    onClose={() => {
                        setCurrentField(undefined);
                        setEditField(undefined);
                        setEditingIndex(undefined);
                    }}
                />

            )}
            {(isAgentText || isRequestorText) && (
                <Confirm
                    shouldShow={id != null}
                    onHide={() => {
                        setCurrentField(undefined);
                        setStaticText(undefined);
                    }}
                    confirmText="Save"
                    onConfirm={() => {
                        setFields(current => {
                            const copy = Array.from(current);
                            const fieldName = currentField?.fieldId;
                            const field = {
                                [fieldName]: staticText,
                            };
                            if (editingIndex != null) {
                                copy[editingIndex] = field;
                            } else {
                                copy.push(field);
                            }

                            return copy;
                        });
                        setCurrentField(undefined);
                        setStaticText(undefined);
                        setEditingIndex(undefined);
                    }}
                    shouldEnforceFocus={false}
                    shouldDisableEscKey
                >
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">
                            {isAgentText ? "Agent visible text" : "Requester visible text"}
                        </Form.Label>
                        <div className="bg-white border-light border rounded">
                            <Editor
                                text={staticText as string}
                                onChange={msg => setStaticText(msg)}
                            />
                        </div>
                        <Form.Text>
                            Static text that can get display in the form. This is useful for
                            sending instructions or other greetings.
                        </Form.Text>
                    </Form.Group>
                </Confirm>
            )}
            <FormDesignerEdit
                field={(!isAgentText && !isRequestorText && editingIndex != null && editFieldId == null)
                    ? currentField
                    : undefined}
                formField={currentField?.fieldId ? fieldMap[currentField?.fieldId] : undefined}
                onHide={() => setEditingIndex(undefined)}
                requestFormType={requestFormType}
                onSave={field => {
                    setFields(current => {
                        const copy = Array.from(current);
                        if (editingIndex != null) {
                            copy[editingIndex] = field;
                        }
                        return copy;
                    });
                    setCurrentField(undefined);
                    setEditingIndex(undefined);
                }}
            />

        </div>
    );
}
FormDesigner.defaultProps = {
    groupIds: undefined,
    requestFormType: "request type",
};
